export const pairKey = (id1, id2) => [id1, id2].filter(Boolean).sort().join('|');

export const winRate = (won, played) => (played > 0 ? (won / played) * 100 : null);

export const vsTeamDelta = (playerRate, teamRate) => {
  if (playerRate == null || teamRate == null) return null;
  return playerRate - teamRate;
};

export const formatWinRate = (rate) => {
  if (rate == null) return '—';
  const rounded = Math.round(rate);
  return `${rounded}%`;
};

export const formatVsTeam = (delta) => {
  if (delta == null) return '—';
  const rounded = Math.round(delta);
  if (rounded > 0) return `+${rounded}%`;
  return `${rounded}%`;
};

const playerSideOnRubber = (rubber, playerId) => {
  if (rubber.home_player_id_1 === playerId || rubber.home_player_id_2 === playerId) return 'home';
  if (rubber.away_player_id_1 === playerId || rubber.away_player_id_2 === playerId) return 'away';
  return null;
};

const partnerIdOnRubber = (rubber, playerId, side) => {
  if (side === 'home') {
    return rubber.home_player_id_1 === playerId ? rubber.home_player_id_2 : rubber.home_player_id_1;
  }
  return rubber.away_player_id_1 === playerId ? rubber.away_player_id_2 : rubber.away_player_id_1;
};

const opponentIdsOnRubber = (rubber, side) => (
  side === 'home'
    ? [rubber.away_player_id_1, rubber.away_player_id_2]
    : [rubber.home_player_id_1, rubber.home_player_id_2]
);

const gamesWonLost = (rubber, side) => {
  let us = 0;
  let them = 0;
  for (const game of rubber.games ?? []) {
    if (game.home == null || game.away == null) continue;
    const homeWon = game.home > game.away;
    if ((side === 'home' && homeWon) || (side === 'away' && !homeWon)) us += 1;
    else them += 1;
  }
  return { us, them };
};

const teamRubberRecord = (fixture, teamId, cardRubbers) => {
  const isHome = fixture.home_team_id === teamId;
  const us = isHome ? fixture.home_score : fixture.away_score;
  const them = isHome ? fixture.away_score : fixture.home_score;
  if (us != null && them != null) return { won: us, lost: them };
  const teamSide = isHome ? 'home' : 'away';
  let won = 0;
  let lost = 0;
  for (const rubber of cardRubbers ?? []) {
    if (rubber.winner === teamSide) won += 1;
    else if (rubber.winner === 'home' || rubber.winner === 'away') lost += 1;
  }
  return won + lost > 0 ? { won, lost } : null;
};

const isCompletedCardFixture = (fixture, cardRubbers) => {
  if (fixture.walkover_label) return false;
  return (cardRubbers ?? []).some((rubber) => rubber.winner === 'home' || rubber.winner === 'away');
};

const sortByDateDesc = (a, b) => {
  const dates = (b.fixture.match_date ?? '').localeCompare(a.fixture.match_date ?? '');
  if (dates !== 0) return dates;
  return (b.rubber.rubber_number ?? 0) - (a.rubber.rubber_number ?? 0);
};

/**
 * Player the analysis panel may show.
 * Members are locked to their own linked league player when that player
 * appeared for the team. Club admins may keep a search selection, then
 * their own link, then the first roster name.
 *
 * @returns {string|null}
 */
export const resolveLeagueAnalysisPlayerId = ({
  canSearchPlayers,
  rosterPlayerIds = [],
  myLeaguePlayerId,
  selectedPlayerId,
}) => {
  const onRoster = (id) => Boolean(id) && rosterPlayerIds.includes(id);
  if (!canSearchPlayers) {
    return onRoster(myLeaguePlayerId) ? myLeaguePlayerId : null;
  }
  if (onRoster(selectedPlayerId)) return selectedPlayerId;
  if (onRoster(myLeaguePlayerId)) return myLeaguePlayerId;
  return rosterPlayerIds[0] ?? null;
};

export const buildLeagueTeamRoster = (lineups, playersById) => {
  const counts = new Map();
  for (const row of lineups ?? []) {
    if (!row.player_id) continue;
    const entry = counts.get(row.player_id) ?? {
      player: playersById.get(row.player_id) ?? null,
      appearances: 0,
    };
    entry.appearances += 1;
    if (!entry.player) entry.player = playersById.get(row.player_id) ?? null;
    counts.set(row.player_id, entry);
  }
  return [...counts.values()]
    .filter((row) => row.player)
    .sort((a, b) => (
      b.appearances - a.appearances
      || String(a.player.display_name ?? '').localeCompare(String(b.player.display_name ?? ''))
    ));
};

export const buildLeaguePlayerAnalysis = ({
  leaguePlayerId,
  teamId,
  fixtures = [],
  rubbers = [],
  lineups = [],
  playersById = new Map(),
}) => {
  const fixtureById = new Map(fixtures.map((fixture) => [fixture.id, fixture]));
  const rubbersByFixture = new Map();
  for (const rubber of rubbers) {
    const list = rubbersByFixture.get(rubber.fixture_id) ?? [];
    list.push(rubber);
    rubbersByFixture.set(rubber.fixture_id, list);
  }

  const completedFixtures = fixtures.filter((fixture) => (
    isCompletedCardFixture(fixture, rubbersByFixture.get(fixture.id))
  ));
  const completedIds = new Set(completedFixtures.map((fixture) => fixture.id));

  const history = [];
  for (const rubber of rubbers) {
    if (!completedIds.has(rubber.fixture_id)) continue;
    const side = playerSideOnRubber(rubber, leaguePlayerId);
    if (!side) continue;
    const fixture = fixtureById.get(rubber.fixture_id);
    if (!fixture) continue;
    const teamSide = fixture.home_team_id === teamId ? 'home' : fixture.away_team_id === teamId ? 'away' : null;
    if (teamSide !== side) continue;
    if (rubber.winner !== 'home' && rubber.winner !== 'away') continue;
    const games = gamesWonLost(rubber, side);
    history.push({
      rubber,
      fixture,
      side,
      won: rubber.winner === side,
      partnerId: partnerIdOnRubber(rubber, leaguePlayerId, side) ?? null,
      opponentIds: opponentIdsOnRubber(rubber, side).filter(Boolean),
      gamesUs: games.us,
      gamesThem: games.them,
    });
  }
  history.sort(sortByDateDesc);

  const playerWon = history.filter((row) => row.won).length;
  const playerLost = history.filter((row) => !row.won).length;
  const playerPlayed = playerWon + playerLost;
  const playerRate = winRate(playerWon, playerPlayed);

  const playerFixtureIds = new Set(history.map((row) => row.fixture.id));
  let teamWon = 0;
  let teamLost = 0;
  for (const fixture of completedFixtures) {
    if (!playerFixtureIds.has(fixture.id)) continue;
    const record = teamRubberRecord(fixture, teamId, rubbersByFixture.get(fixture.id));
    if (!record) continue;
    teamWon += record.won;
    teamLost += record.lost;
  }
  const teamPlayed = teamWon + teamLost;
  const teamRate = winRate(teamWon, teamLost + teamWon);
  const vsTeam = vsTeamDelta(playerRate, teamRate);

  const partnerMap = new Map();
  for (const row of history) {
    if (!row.partnerId) continue;
    const entry = partnerMap.get(row.partnerId) ?? {
      partnerId: row.partnerId,
      player: playersById.get(row.partnerId) ?? null,
      won: 0,
      lost: 0,
      fixtureIds: new Set(),
      history: [],
    };
    if (row.won) entry.won += 1;
    else entry.lost += 1;
    entry.fixtureIds.add(row.fixture.id);
    entry.history.push(row);
    partnerMap.set(row.partnerId, entry);
  }

  const partners = [...partnerMap.values()]
    .map((entry) => {
      const played = entry.won + entry.lost;
      const rate = winRate(entry.won, played);
      let sharedTeamWon = 0;
      let sharedTeamLost = 0;
      for (const fixtureId of entry.fixtureIds) {
        const fixture = fixtureById.get(fixtureId);
        const record = teamRubberRecord(fixture, teamId, rubbersByFixture.get(fixtureId));
        if (!record) continue;
        sharedTeamWon += record.won;
        sharedTeamLost += record.lost;
      }
      const sharedTeamPlayed = sharedTeamWon + sharedTeamLost;
      return {
        partnerId: entry.partnerId,
        player: entry.player,
        won: entry.won,
        lost: entry.lost,
        played,
        winRate: rate,
        teamWon: sharedTeamWon,
        teamLost: sharedTeamLost,
        vsTeam: vsTeamDelta(rate, winRate(sharedTeamWon, sharedTeamPlayed)),
        history: [...entry.history].sort(sortByDateDesc),
      };
    })
    .sort((a, b) => (
      b.played - a.played
      || (b.vsTeam ?? -999) - (a.vsTeam ?? -999)
      || String(a.player?.display_name ?? '').localeCompare(String(b.player?.display_name ?? ''))
    ));

  const appearanceIds = new Set(
    (lineups ?? [])
      .filter((row) => row.player_id === leaguePlayerId)
      .map((row) => row.fixture_id),
  );

  return {
    player: playersById.get(leaguePlayerId) ?? null,
    appearances: appearanceIds.size,
    rubbers: {
      won: playerWon,
      lost: playerLost,
      played: playerPlayed,
      winRate: playerRate,
    },
    team: {
      won: teamWon,
      lost: teamLost,
      played: teamPlayed,
      winRate: teamRate,
    },
    vsTeam,
    partners,
    history,
  };
};
